import { PageResult } from '@/core/request/PageResult';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { SelectProps } from 'antd';
import { useEffect } from 'react';
import {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from 'react-query';

export interface SelectOption {
  value: string | number;
  label: React.ReactNode;
  disabled?: boolean;
}

type LabelFormatter<T, D> = (item: T | D) => React.ReactNode;
type DisableCondition<T, D> = (item: T | D) => boolean;

export interface Props<T extends { id: number }, D extends { id: number }> {
  list?: UseInfiniteQueryResult<PageResult<T>, unknown>;
  directList?: UseQueryResult<T[], unknown>;
  detail?:
    | UseMutationResult<D, unknown, number, unknown>
    | UseQueryResult<D | undefined, unknown>;
  selectProps: SelectProps;
  labelFormatter: LabelFormatter<T, D>;
  disableCondition?: DisableCondition<T, D>;
}

function isMutationResult<T extends { id: number }, D extends { id: number }>(
  detail: Props<T, D>['detail'],
): detail is UseMutationResult<D, unknown, number, unknown> {
  return !!detail && 'mutateAsync' in detail;
}

const useSelectOptions = <T extends { id: number }, D extends { id: number }>(
  props: Props<T, D>,
): SelectOption[] => {
  const {
    list,
    directList,
    detail,
    selectProps,
    labelFormatter,
    disableCondition,
  } = props;

  const dataList = list ? flatInfiniteData(list.data) : directList?.data;
  const dataDetail = detail?.data ?? undefined;

  const isDetailInList =
    dataDetail && dataList?.some((item) => item.id === dataDetail.id);

  const options = [
    ...(dataDetail && !isDetailInList ? [dataDetail] : []),
    ...(dataList || []),
  ];

  const value = selectProps?.value;
  const isMultiple = selectProps?.mode === 'multiple';

  const loadingList = list?.isLoading;
  const loadingDirectList = directList?.isLoading;
  const loadingDetail = detail?.isLoading;

  useEffect(() => {
    if (
      !!value &&
      (!loadingList || !loadingDirectList) &&
      !options.find((option) => option.id === value) &&
      !isMultiple &&
      (!dataDetail || dataDetail.id !== value) &&
      isMutationResult(detail)
    ) {
      detail.mutateAsync(value);
    }
  }, [value]);

  if (loadingList || loadingDirectList) {
    return [{ value: '', label: 'Carregando...' }];
  }

  if (!!value && loadingDetail) {
    return [
      {
        value: value,
        label: dataDetail ? labelFormatter(dataDetail) : 'Carregando...',
      },
      { value: '', label: 'Carregando...' },
    ];
  }

  return options.map((item) => ({
    value: item.id,
    label: labelFormatter(item),
    disabled: disableCondition ? disableCondition(item) : false,
  }));
};

export default useSelectOptions;
